import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaCheckCircle } from 'react-icons/fa'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import api from 'services/api'

import { useUser } from 'contexts/user.context'

import ProfileItineraryCard from 'components/profileItineraryCard'
import ProfileTravelMap from 'components/profileTravelMap'
import RankingIcon from 'components/rankingIcon'
import TravelInterestIcon from 'components/travelInterestIcon'

import * as P from './modernProfile'

const DEFAULT_TEXT =
  'Viajante RedSterna que busca viver e compartilhar experiências incríveis ao redor do mundo! Siga-me para acompanhar minha jornada.'

const normalizeCityName = (name: unknown) =>
  String(name ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

const countDestinations = (itineraries: any[] = []) => {
  const destinations = new Set<string>()

  itineraries.forEach((itinerary) => {
    ;(itinerary.cities ?? []).forEach((city: any) => {
      const cityName = normalizeCityName(city.name)

      if (cityName) {
        destinations.add(cityName)
        return
      }

      const latitude = Number(city.latitude)
      const longitude = Number(city.longitude)

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        destinations.add(`${latitude.toFixed(4)}|${longitude.toFixed(4)}`)
      }
    })
  })

  return destinations.size
}

const getSternaPreposition = (gender?: string) => {
  if (gender === 'F') return 'da'
  if (gender === 'M') return 'do'
  return 'de'
}

const Profile = () => {
  const { user, getUserData } = useUser()
  const history = useHistory()
  const queryClient = useQueryClient()
  const [aboutExpanded, setAboutExpanded] = useState(false)
  const [itineraryToDelete, setItineraryToDelete] = useState<any | null>(null)
  const [deletingItinerary, setDeletingItinerary] = useState(false)

  useEffect(() => {
    getUserData()
  }, [getUserData])

  const { data: profileDetails } = useQuery({
    queryKey: ['profileDetails', user?.id],
    queryFn: async () => {
      const response = await api.get(`/users/${user.id}`)
      return response.data
    },
    enabled: !!user?.id,
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const {
    data: itineraries = [],
    isPending: itinerariesPending,
    isFetching: itinerariesFetching,
    isError: itinerariesError,
    refetch: refetchItineraries
  } = useQuery({
    queryKey: ['itinerariesUser', user?.id],
    queryFn: async () => {
      const response = await api.get(`/itineraries/user/${user.id}`)
      return response.data
    },
    enabled: !!user?.id
  })

  const { data: following = [] } = useQuery({
    queryKey: ['followingUser', user?.id],
    queryFn: async () => {
      const response = await api.get(`/user-follower/following/${user.id}`)
      return response.data
    },
    enabled: !!user?.id
  })

  const { data: followers = [] } = useQuery({
    queryKey: ['followersUser', user?.id],
    queryFn: async () => {
      const response = await api.get(`/user-follower/followers/${user.id}`)
      return response.data
    },
    enabled: !!user?.id
  })

  const { data: inspirationsCount = 0 } = useQuery({
    queryKey: ['inspirationsCount', user?.id],
    queryFn: async () => {
      const response = await api.get(
        `/itineraries/user/${user.id}/inspirations-count`
      )
      return Number(response.data?.count ?? 0)
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000
  })

  const { data: favoriteItineraries = [] } = useQuery({
    queryKey: ['favoriteItineraries', user?.id],
    queryFn: async () => {
      const response = await api.get(`/itineraries/user/favorite/${user.id}`)
      return response.data
    },
    enabled: !!user?.id
  })

  if (!user) return <></>

  const displayedUser = profileDetails ?? user
  const interests = (profileDetails?.interests ?? user.interests ?? []).map(
    (item: any) => (item?.interest ? item.interest : item)
  )
  const inspiredItineraries = itineraries.filter((itinerary: any) =>
    Boolean(itinerary.inspiredFromItineraryId)
  )
  const publishedItineraries = itineraries.filter(
    (itinerary: any) => !itinerary.inspiredFromItineraryId && itinerary.made
  )
  const plannedItineraries = itineraries.filter(
    (itinerary: any) => !itinerary.inspiredFromItineraryId && !itinerary.made
  )
  const destinationCount = countDestinations(itineraries)
  const about = displayedUser.about?.trim() || DEFAULT_TEXT
  const preposition = getSternaPreposition(displayedUser.gender)

  const handleDeleteItinerary = (itinerary: any) => {
    setItineraryToDelete(itinerary)
  }

  const confirmDeleteItinerary = async () => {
    if (!itineraryToDelete || deletingItinerary) return

    setDeletingItinerary(true)

    try {
      await api.delete(`/itineraries/${itineraryToDelete.id}`)
      toast.success('Roteiro excluído com sucesso.', { theme: 'dark' })
      setItineraryToDelete(null)

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['itinerariesUser', user.id]
        }),
        queryClient.invalidateQueries({
          queryKey: ['favoriteItineraries', user.id]
        }),
        queryClient.invalidateQueries({ queryKey: ['AllItineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['Itineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['inspirationStatus'] })
      ])
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível excluir o roteiro.',
        { theme: 'dark' }
      )
    } finally {
      setDeletingItinerary(false)
    }
  }

  return (
    <P.Page>
      <P.TopLabel>
        Sterna {preposition} {displayedUser.firstname}
      </P.TopLabel>

      <P.Cover>
        <P.CoverImage
          src={displayedUser.coverPicture || '/images/cover-empty.png'}
          alt="Foto de capa"
          onError={(event) => {
            const fallback = '/images/cover-empty.png'
            if (!event.currentTarget.src.endsWith(fallback)) {
              event.currentTarget.src = fallback
            }
          }}
        />
        <P.SearchButton onClick={() => history.push('/buscar-usuarios')}>
          Procurar usuário
          <img src="/images/user-search.svg" alt="Buscar usuários" />
        </P.SearchButton>
      </P.Cover>

      <P.IdentityRow>
        <P.Avatar
          src={displayedUser.profilePicture || '/images/profile-empty.png'}
          alt="Foto de perfil"
        />
        <P.Identity>
          <P.NameLine>
            <P.Name>Bem vindo, {displayedUser.firstname}!</P.Name>
            {displayedUser.verified && (
              <P.VerifiedBadge title="Perfil verificado pela RedSterna">
                <FaCheckCircle />
                Verificado
              </P.VerifiedBadge>
            )}
          </P.NameLine>
          <P.Subtitle>
            Explorando o mundo, um roteiro de cada vez. ✈️
          </P.Subtitle>
        </P.Identity>

        <P.Stats>
          <P.Stat>
            <strong>{followers.length}</strong>
            <span>Seguidores</span>
          </P.Stat>
          <P.Stat>
            <strong>{following.length}</strong>
            <span>Seguindo</span>
          </P.Stat>
          <P.Stat>
            <strong>{itineraries.length}</strong>
            <span>Roteiros</span>
          </P.Stat>
          <P.Stat>
            <strong>{destinationCount}</strong>
            <span>Destinos</span>
          </P.Stat>
          <P.InspirationStat>
            <strong>{inspirationsCount}</strong>
            <span>Inspirações</span>
          </P.InspirationStat>
        </P.Stats>
      </P.IdentityRow>

      <P.InfoRibbon>
        <P.InfoBlock>
          <P.InfoContent>
            <P.InfoTitle>Sobre mim</P.InfoTitle>
            <P.AboutText
              type="button"
              $expanded={aboutExpanded}
              aria-expanded={aboutExpanded}
              title={
                aboutExpanded ? 'Clique para recolher' : 'Clique para ler tudo'
              }
              onClick={() => setAboutExpanded((expanded) => !expanded)}
            >
              {about}
            </P.AboutText>
          </P.InfoContent>
        </P.InfoBlock>

        <P.RankBlock>
          <P.InfoTitle>Perfil</P.InfoTitle>
          <RankingIcon itinerariesLength={itineraries.length} />
        </P.RankBlock>

        <P.InfoBlock>
          <P.InfoContent>
            <P.InfoTitle>Interesses</P.InfoTitle>
            <P.Interests>
              {interests.map((interest: any) => {
                const value = interest.name
                const label = interest.description || value

                return (
                  <P.Interest
                    key={interest.id ?? value}
                    title={label}
                    aria-label={label}
                  >
                    <TravelInterestIcon value={value} title={label} />
                  </P.Interest>
                )
              })}
            </P.Interests>
          </P.InfoContent>
        </P.InfoBlock>
      </P.InfoRibbon>

      <ProfileTravelMap
        itineraries={itineraries}
        ownerName={displayedUser.firstname}
      />

      <P.Section>
        <P.SectionHeader>
          <h2>
            Roteiros publicados (
            {itinerariesPending ? '…' : publishedItineraries.length})
          </h2>
          <P.CreateButton to="/criar-roteiro">
            + Criar novo roteiro
          </P.CreateButton>
        </P.SectionHeader>
        {itinerariesPending || (itinerariesFetching && !itineraries.length) ? (
          <P.Empty role="status">
            Carregando seus roteiros publicados...
          </P.Empty>
        ) : itinerariesError ? (
          <P.Empty role="alert">
            Não foi possível carregar seus roteiros.{' '}
            <button type="button" onClick={() => refetchItineraries()}>
              Tentar novamente
            </button>
          </P.Empty>
        ) : publishedItineraries.length > 0 ? (
          <P.CardsGrid>
            {publishedItineraries.map((itinerary: any) => (
              <ProfileItineraryCard
                key={itinerary.id}
                itinerary={itinerary}
                canManage
                canDelete={itinerary.authorId === user.id}
                onDelete={handleDeleteItinerary}
              />
            ))}
          </P.CardsGrid>
        ) : (
          <P.Empty>Nenhum roteiro publicado.</P.Empty>
        )}
      </P.Section>

      <P.Section>
        <P.SectionHeader>
          <div>
            <h2>
              Roteiros planejados (
              {itinerariesPending ? '…' : plannedItineraries.length})
            </h2>
            <P.SectionDescription>
              Visível somente para você. Organize aqui as viagens que ainda está
              planejando.
            </P.SectionDescription>
          </div>
        </P.SectionHeader>
        {itinerariesPending || (itinerariesFetching && !itineraries.length) ? (
          <P.Empty role="status">
            Carregando seus roteiros planejados...
          </P.Empty>
        ) : itinerariesError ? (
          <P.Empty role="alert">
            Não foi possível carregar seus roteiros.{' '}
            <button type="button" onClick={() => refetchItineraries()}>
              Tentar novamente
            </button>
          </P.Empty>
        ) : plannedItineraries.length > 0 ? (
          <P.CardsGrid>
            {plannedItineraries.map((itinerary: any) => (
              <ProfileItineraryCard
                key={itinerary.id}
                itinerary={itinerary}
                canManage
                canDelete={itinerary.authorId === user.id}
                onDelete={handleDeleteItinerary}
              />
            ))}
          </P.CardsGrid>
        ) : (
          <P.Empty>Nenhum roteiro planejado.</P.Empty>
        )}
      </P.Section>

      {inspiredItineraries.length > 0 && (
        <P.InspiredSection>
          <P.SectionHeader>
            <div>
              <h2>Roteiros Inspirados ({inspiredItineraries.length})</h2>
              <P.SectionDescription>
                Ideias que você descobriu na comunidade e trouxe para planejar
                do seu jeito.
              </P.SectionDescription>
            </div>
          </P.SectionHeader>
          <P.CardsGrid>
            {inspiredItineraries.map((itinerary: any) => (
              <ProfileItineraryCard
                key={itinerary.id}
                itinerary={itinerary}
                canManage
                canDelete
                onDelete={handleDeleteItinerary}
              />
            ))}
          </P.CardsGrid>
        </P.InspiredSection>
      )}

      {favoriteItineraries.length > 0 && (
        <P.Section>
          <P.SectionHeader>
            <h2>Roteiros favoritos ({favoriteItineraries.length})</h2>
          </P.SectionHeader>
          <P.CardsGrid>
            {favoriteItineraries.map((itinerary: any) => (
              <ProfileItineraryCard key={itinerary.id} itinerary={itinerary} />
            ))}
          </P.CardsGrid>
        </P.Section>
      )}
      {itineraryToDelete && (
        <P.DeleteModalOverlay
          role="presentation"
          onMouseDown={() => !deletingItinerary && setItineraryToDelete(null)}
        >
          <P.DeleteModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-itinerary-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <P.DeleteModalIcon>!</P.DeleteModalIcon>
            <P.DeleteModalContent>
              <h3 id="delete-itinerary-title">Excluir roteiro?</h3>
              <p>
                Você está prestes a excluir “{itineraryToDelete.title}”. Essa
                ação remove o roteiro e suas cidades do mapa quando elas não
                estiverem presentes em outros roteiros.
              </p>
            </P.DeleteModalContent>
            <P.DeleteModalActions>
              <button
                type="button"
                onClick={() => setItineraryToDelete(null)}
                disabled={deletingItinerary}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="danger"
                onClick={confirmDeleteItinerary}
                disabled={deletingItinerary}
              >
                {deletingItinerary ? 'Excluindo...' : 'Excluir roteiro'}
              </button>
            </P.DeleteModalActions>
          </P.DeleteModal>
        </P.DeleteModalOverlay>
      )}
    </P.Page>
  )
}

export default Profile
